"use client";

import { CheckCircle, Clock, Eye, XCircle, Send, AlertCircle } from "lucide-react";
import { formatDateTime } from "@/lib/formatting";
import type { SignatureStatus as SignatureStatusType } from "@/lib/deals/signature";

interface SignatureStatusProps {
  role: "seller" | "buyer";
  status: SignatureStatusType | null;
  signedAt?: string | null;
  className?: string;
}

const STATUS_CONFIG: Record<
  SignatureStatusType,
  {
    icon: React.ComponentType<{ className?: string }>;
    bg: string;
    text: string;
    label: string;
    description: string;
  }
> = {
  pending: {
    icon: Clock,
    bg: "bg-zinc-50",
    text: "text-zinc-600",
    label: "Ausstehend",
    description: "Warten auf Versand des Vertrags",
  },
  sent: {
    icon: Send,
    bg: "bg-blue-50",
    text: "text-blue-700",
    label: "Gesendet",
    description: "Vertrag wurde zur Unterschrift gesendet",
  },
  viewed: {
    icon: Eye,
    bg: "bg-amber-50",
    text: "text-amber-700",
    label: "Angesehen",
    description: "Vertrag wurde geöffnet",
  },
  signed: {
    icon: CheckCircle,
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    label: "Unterschrieben",
    description: "Vertrag wurde unterschrieben",
  },
  declined: {
    icon: XCircle,
    bg: "bg-red-50",
    text: "text-red-700",
    label: "Abgelehnt",
    description: "Unterschrift wurde abgelehnt",
  },
};

/**
 * SignatureStatus - Displays the signature status for a party
 */
export function SignatureStatus({ role, status, signedAt, className = "" }: SignatureStatusProps) {
  const roleLabel = role === "seller" ? "Verkäufer" : "Käufer";

  if (!status) {
    return (
      <div className={`rounded-lg border border-zinc-200 bg-zinc-50 p-4 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-zinc-500" />
          </div>
          <div>
            <p className="font-medium text-zinc-700">{roleLabel}</p>
            <p className="text-sm text-zinc-500">Status nicht verfügbar</p>
          </div>
        </div>
      </div>
    );
  }

  const config = STATUS_CONFIG[status];
  const StatusIcon = config.icon;

  return (
    <div className={`rounded-lg border border-zinc-200 ${config.bg} p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}
        >
          <StatusIcon className={`w-5 h-5 ${config.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`font-medium ${config.text}`}>{roleLabel}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}>
              {config.label}
            </span>
          </div>
          <p className="text-sm text-zinc-600 mt-0.5">{config.description}</p>

          {status === "signed" && signedAt && (
            <p className="text-sm text-zinc-500 mt-2">
              Unterschrieben am {formatDateTime(signedAt)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
